import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { CollapsibleSection, ManifestRowsScroller, ManifestRowsTableHead } from './ManifestRowsTable';
import { buildCellErrorOverlay, type CellOverlay } from '../../helpers/buildCellErrorOverlay';
import type { PreviewColumn, PreviewRow } from '../../state/api/organisationEngineSlice';

type ErrorRowsTableProps = {
    errorRows: Array<Extract<PreviewRow, { status: 'error' }>>;
    columns: PreviewColumn[];
};

export const ErrorRowsTable = ({ errorRows, columns }: ErrorRowsTableProps) => (
    <CollapsibleSection
        testId='preview-error-rows'
        title={`${errorRows.length} ${errorRows.length === 1 ? 'row' : 'rows'} needing attention`}
        description='These rows have validation errors and will not be onboarded. Fix the CSV and re-upload to retry.'
        defaultOpen
    >
        <ManifestRowsScroller>
            <table className='w-full border-collapse text-sm'>
                <ManifestRowsTableHead columns={columns} />
                <tbody>
                    {errorRows.map(errorRow => (
                        <ErrorRow key={errorRow.row} errorRow={errorRow} columns={columns} />
                    ))}
                </tbody>
            </table>
        </ManifestRowsScroller>
    </CollapsibleSection>
);

const ErrorRow = ({ errorRow, columns }: { errorRow: Extract<PreviewRow, { status: 'error' }>; columns: PreviewColumn[] }) => {
    const overlay = useMemo(() => buildCellErrorOverlay(errorRow.errors, columns), [errorRow.errors, columns]);

    // `odd:bg-grey-10` zebra-stripes the body rows so left-to-right tracking
    // across the wide manifest table stays comfortable.
    return (
        <tr data-testid={`error-row-${errorRow.row}`} className='border-b border-grey-20 align-top odd:bg-grey-10'>
            <td className='p-2 font-mono text-grey-70'>{errorRow.row}</td>
            {columns.map((col, colIndex) => (
                <Cell key={col.key} value={errorRow.cells[col.key] ?? ''} overlay={overlay[colIndex]} columnIndex={colIndex} />
            ))}
        </tr>
    );
};

// Cell values render in monospace — signals "this is the literal CSV data".
// Engine error messages render in italic with a leading ⚠ icon — visually
// shifts them into "meta-commentary about the cell" rather than blending
// with the data above.
const Cell = ({ value, overlay, columnIndex }: { value: string; overlay: CellOverlay | undefined; columnIndex: number }) => {
    const isBad = overlay?.bordered ?? false;
    const cellClass = ['p-2', isBad ? 'border-l-4 border-red-100' : ''].filter(Boolean).join(' ');
    const valueClass = [
        'font-mono text-sm',
        value === '' ? 'italic text-grey-70' : isBad ? 'text-red-100 font-semibold' : 'text-grey-100',
    ].join(' ');
    const displayValue = value === '' ? '—' : value;

    return (
        <td className={cellClass} data-testid={isBad ? `error-cell-${columnIndex}` : undefined} data-cell-bad={isBad ? 'true' : undefined}>
            <div className={valueClass}>{displayValue}</div>
            {overlay?.messages.map((msg, idx) => (
                <div
                    key={idx}
                    className='mt-1 flex items-start gap-1 text-xs italic text-red-100'
                    data-testid={`error-message-${columnIndex}`}
                >
                    <AlertTriangle className='h-3 w-3 mt-0.5 shrink-0' aria-hidden='true' />
                    <span>{msg}</span>
                </div>
            ))}
        </td>
    );
};
